/* import __COLOCATED_TEMPLATE__ from './channel-summary.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { type ComponentLike } from '@glint/template';
import { inject as service } from '@ember/service';
import { taskFor } from 'ember-concurrency-ts';
import { task } from 'ember-concurrency-decorators';
import { get } from 'embercom/lib/ajax';
import { objectTypes } from 'embercom/models/data/matching-system/matching-constants';
import CustomBotListData from 'embercom/objects/operator/configuration/custom-bots/custom-bot-list-data';
import type { BotConfigTarget } from 'embercom/objects/operator/configuration/configuration';
import { CUSTOM_BOT_CONFIG } from 'embercom/objects/operator/configuration/configuration';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import type Store from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import { statisticKeys } from 'embercom/models/data/outbound/constants';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import { isEmpty } from '@ember/utils';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { tracked } from '@glimmer/tracking';
import { type ReactRouteArgs } from 'embercom/components/standalone/deploy/overview/channel-options';

import { CUSTOM_BOT_REQUIRED_PERMISSION } from 'embercom/lib/operator/custom-bots/constants';

export interface Args {
  title: string;
  channelIcon: InterfaceIconName;
  manageRoute?: string;
  manageRouteReact?: ReactRouteArgs;
  workflowRoute: string;
  workflowParams: {
    target: BotConfigTarget;
    objectType: number;
  };
  statusPill?: ComponentLike;
  allowNoWorkflows?: boolean;
}

interface Signature {
  Args: Args;
  Element: HTMLDivElement;
}

export default class StandaloneDeployOverviewChannelSummary extends Component<Signature> {
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare notificationsService: any;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare permissionsService: any;

  @tracked hoveredRowId: number | string | null = null;
  @tracked showDeleteModal = false;
  @tracked workflowContentWrapperToDelete: any = null;
  @tracked dependentObjects: any[] = [];
  @tracked isLastLive = false;

  listData: CustomBotListData;
  sortableGroupName: string;

  constructor(owner: any, args: Args) {
    super(owner, args);
    // This is used to group the workflows in the table so that they can be reordered.
    // Needed to use a GUID to avoid conflicts with other tables: https://stackoverflowteams.com/c/intercom/questions/1425/when-using-animations-and-ember-sortable-why-do-i-get-a-cannot-read-properties
    this.sortableGroupName = `channel-summary-workflows-${guidFor(this)}`;
    this.listData = new CustomBotListData(
      owner,
      this.appService.app.id,
      this.args.workflowParams.target,
      this.args.workflowParams.objectType,
      true,
      [statisticKeys.receipts, statisticKeys.completions],
    );

    taskFor(this.listData.setupInitialState).perform();
  }

  get isDeleteBlocked() {
    return this.isLastLive;
  }

  get isLoadingInitialData() {
    return taskFor(this.listData.setupInitialState).isRunning && isEmpty(this.data);
  }

  get workflowTargetConfig() {
    return CUSTOM_BOT_CONFIG[this.args.workflowParams.target];
  }

  get workflowContentWrappers() {
    return (
      this.listData.bots ??
      ([] as Array<{
        id: string;
        contentWrapperId: string;
        title: string;
        contents: Array<{ contentData: any }>;
        priority: number;
      }>)
    );
  }

  get data() {
    return (this.workflowContentWrappers || []).sort(
      (a: ContentWrapper, b: ContentWrapper) => (a.priority || 0) - (b.priority || 0),
    );
  }

  get sentStatKey() {
    return statisticKeys.receipts;
  }

  get completedStatKey() {
    return statisticKeys.completions;
  }

  get columns() {
    return [
      { valuePath: 'priority', width: '5%' },
      {
        label: this.intl.t('standalone.deploy.overview.workflow-table.status'),
        valuePath: 'state',
        width: '10%',
      },
      {
        label: this.intl.t('standalone.deploy.overview.workflow-table.workflow'),
        valuePath: 'title',
        width: '30%',
      },
      {
        label: this.intl.t('standalone.deploy.overview.workflow-table.last-updated-at'),
        valuePath: 'updatedAt',
        width: '13%',
      },
      {
        label: this.intl.t('standalone.deploy.overview.workflow-table.last-updated-by'),
        valuePath: 'lastUpdatedBy',
        width: '17%',
      },
      {
        label: this.intl.t('standalone.deploy.overview.workflow-table.sent'),
        valuePath: 'sentCount',
        width: '8%',
        tooltip: this.intl.t('standalone.deploy.overview.workflow-table.sent-tooltip'),
      },
      {
        label: this.intl.t('standalone.deploy.overview.workflow-table.completed'),
        valuePath: 'completedPercent',
        width: '12%',
        tooltip: this.intl.t('standalone.deploy.overview.workflow-table.completed-tooltip'),
      },
      {
        valuePath: 'actions',
        width: '5%',
      },
    ];
  }

  get adminCanNotManageBots(): boolean {
    return !this.permissionsService.currentAdminCan(CUSTOM_BOT_REQUIRED_PERMISSION);
  }

  @action
  shouldDisableWorkflowManagement(workflowContentWrapper: ContentWrapper) {
    if (this.args.allowNoWorkflows) {
      return false;
    }

    // The first (highest priority) workflow should be the default workflow
    let firstWorkflowContentWrapper = this.data[0];
    return workflowContentWrapper === firstWorkflowContentWrapper || this.adminCanNotManageBots;
  }

  @action
  setHoveredRow(workflowContentWrapper: ContentWrapper) {
    this.hoveredRowId = workflowContentWrapper?.contentWrapperId || null;
  }

  @action
  clearHoveredRow() {
    this.hoveredRowId = null;
  }

  @action
  isRowHovered(workflowContentWrapper: ContentWrapper) {
    return this.hoveredRowId === workflowContentWrapper?.contentWrapperId;
  }

  @task({ drop: true })
  *destroyWorkflowTask(workflowContentWrapper: ContentWrapper) {
    try {
      // Loading ruleset and deleting it was causing reactivity conflicts
      // so we're using the adapter to delete it directly
      let adapter = (this.store as any).adapterFor('matching-system/ruleset');
      yield adapter.deleteRecord(this.store, this.store.modelFor('matching-system/ruleset'), {
        id: workflowContentWrapper.contentWrapperId,
      });

      yield taskFor(this.listData.reload).perform();

      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.deploy.overview.delete.success'),
      );
    } catch (error) {
      this.notificationsService.notifyError(this.intl.t('standalone.deploy.overview.delete.fail'));
    }
  }

  @action
  destroyWorkflow(workflowContentWrapper: ContentWrapper) {
    taskFor(this.destroyWorkflowTask).perform(workflowContentWrapper);
  }

  @action
  async createNewWorkflow() {
    let workflowTargetConfig = this.workflowTargetConfig;

    let rulesetParams = {
      app_id: this.appService.app.id,
      object_type: workflowTargetConfig.objectType,
      match_behavior: workflowTargetConfig.matchBehavior,
      role_predicate_group: workflowTargetConfig.rolePredicateGroup,
      object_data: {
        type: workflowTargetConfig.botType,
        trigger_type: workflowTargetConfig.trigger,
        bot_data: { created_from_standalone_app: true },
      },
      workflowRoute: this.args.workflowRoute,
    };

    this.router.transitionTo('apps.app.content.new', rulesetParams);
  }

  @action
  async reorder(updatedPriorities: ContentWrapper[], draggedWrapper: ContentWrapper) {
    // Largely a re-implementation of the reorder method in app/components/operator/custom-bots/collapsible-list.js
    // apart from the logic to prevent the first workflow from being moved from its position

    // Prevent any workflow from being dragged above the first workflow
    let firstWorkflowContentWrapper = this.data[0];
    let newFirstWorkflowContentWrapper = updatedPriorities[0];

    if (
      firstWorkflowContentWrapper &&
      newFirstWorkflowContentWrapper &&
      firstWorkflowContentWrapper !== newFirstWorkflowContentWrapper &&
      !this.args.allowNoWorkflows
    ) {
      // If the first workflow is being moved from its position, reject the reorder
      this.notificationsService.notifyError(
        this.intl.t('standalone.deploy.overview.order-priority.first-workflow-protected'),
      );
      return;
    }

    try {
      this.data.forEach((workflowContentWrapper: any) => {
        workflowContentWrapper.set('priority', updatedPriorities.indexOf(workflowContentWrapper));
      });

      let ruleset = await this.store.findRecord(
        'matching-system/ruleset',
        draggedWrapper.contentWrapperId,
      );

      let draggedWorkflowContentWrapperIndex = updatedPriorities.indexOf(draggedWrapper);
      let previousWorkflowContentWrapper =
        updatedPriorities[draggedWorkflowContentWrapperIndex - 1];
      let nextWorkflowContentWrapper = updatedPriorities[draggedWorkflowContentWrapperIndex + 1];

      let response = await ruleset.reorder(
        previousWorkflowContentWrapper?.contentWrapperId,
        nextWorkflowContentWrapper?.contentWrapperId,
      );

      // Map ruleset id to priority.
      let rulesetPriorityMapping = response.reduce((m: any, rulesetPriority: any) => {
        m[rulesetPriority.ruleset_id] = rulesetPriority.priority;
        return m;
      }, {});

      // Update the content wrappers in the store to have the correct priority
      // from the backend response, or clean them up if we don't get them in
      // the response.
      this.store.peekAll('outbound/content-wrapper').forEach((wrapper: any) => {
        let id = wrapper.contentWrapperId;
        if (id in rulesetPriorityMapping) {
          wrapper.set('priority', rulesetPriorityMapping[id]);
        } else {
          wrapper.rollbackAttributes();
        }
      });

      // Store the new priorities in the store.
      this.store.pushPayload({
        'outbound/content-wrapper': this.store
          .peekAll('outbound/content-wrapper')
          .filter((wrapper: any) => wrapper.hasDirtyAttributes)
          .map((wrapper: any) => wrapper.toJSON({ includeId: true })),
      });

      this.notificationsService.notifyConfirmation(
        this.intl.t(`standalone.deploy.overview.order-priority.success`),
      );
    } catch {
      this.notificationsService.notifyError(
        this.intl.t(`standalone.deploy.overview.order-priority.fail`),
      );

      // If there were any errors, let's rollback all the content wrappers
      // that were updated so that they aren't dirty.
      this.data
        .filter((workflowContentWrapper) => workflowContentWrapper.hasDirtyAttributes)
        .forEach((workflowContentWrapper) => workflowContentWrapper.rollbackAttributes());
    }
  }

  @task({ drop: true })
  *openDeleteModalTask(workflowContentWrapper: ContentWrapper): Generator<any, void> {
    this.workflowContentWrapperToDelete = workflowContentWrapper;
    try {
      let [dependents, lastLiveResponse] = yield Promise.all([
        get('/ember/content_service/contents/list_dependents', {
          app_id: this.appService.app.id,
          target_entity_id: workflowContentWrapper.contentWrapperId,
          target_entity_type: objectTypes.ruleset,
        }),
        get('/ember/standalone/channel_workflows/last_live', {
          workflow_id: workflowContentWrapper.contentWrapperId,
        }),
      ]);
      this.dependentObjects = dependents;
      this.isLastLive = lastLiveResponse.last_live;
    } catch {
      this.dependentObjects = [];
      this.isLastLive = false;
    }
    this.showDeleteModal = true;
  }

  @action
  openDeleteModal(workflowContentWrapper: ContentWrapper) {
    taskFor(this.openDeleteModalTask).perform(workflowContentWrapper);
  }

  @action
  closeDeleteModal() {
    this.showDeleteModal = false;
    this.workflowContentWrapperToDelete = null;
    this.dependentObjects = [];
    this.isLastLive = false;
  }

  @action
  confirmDeleteWorkflow() {
    if (this.workflowContentWrapperToDelete) {
      this.destroyWorkflow(this.workflowContentWrapperToDelete);
    }
    this.closeDeleteModal();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Deploy::Overview::ChannelSummary': typeof StandaloneDeployOverviewChannelSummary;
    'standalone/deploy/overview/channel-summary': typeof StandaloneDeployOverviewChannelSummary;
  }
}
